import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface Device {
  id: string;
  name: string;
  is_online: boolean;
  active_violations: number;
}

interface Venue {
  id: string;
  name: string;
  devices: Device[];
  online_count: number;
}

interface NetworkGraphProps {
  venues: Venue[];
  width?: number;
  height?: number;
}

const NetworkGraph: React.FC<NetworkGraphProps> = ({
  venues,
  width = 800,
  height = 600,
}) => {
  const [selectedVenue, setSelectedVenue] = useState<string | null>(null);
  const [hoveredDevice, setHoveredDevice] = useState<string | null>(null);

  // Calculate positions for venues in a circular layout
  const venuePositions = venues.map((venue, index) => {
    const angle = (index / venues.length) * 2 * Math.PI;
    const radius = Math.min(width, height) * 0.3;
    const x = width / 2 + radius * Math.cos(angle);
    const y = height / 2 + radius * Math.sin(angle);
    return { x, y, venue };
  });

  // Calculate device positions around their parent venue
  const getDevicePositions = (venueX: number, venueY: number, devices: Device[]) => {
    return devices.map((device, index) => {
      const angle = (index / devices.length) * 2 * Math.PI;
      const radius = 80;
      const x = venueX + radius * Math.cos(angle);
      const y = venueY + radius * Math.sin(angle);
      return { x, y, device };
    });
  };

  const getStatusColor = (venue: Venue) => {
    const hasViolations = venue.devices.some(d => d.active_violations > 0);
    const onlinePercentage = venue.devices.length > 0
      ? venue.online_count / venue.devices.length
      : 0;

    if (hasViolations) return '#EF4444'; // Red
    if (onlinePercentage < 0.5) return '#F59E0B'; // Yellow
    if (onlinePercentage >= 0.9) return '#10B981'; // Green
    return '#6B7280'; // Gray
  };

  return (
    <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Network Topology
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Venue and device network visualization
        </p>
      </div>

      <svg width={width} height={height} className="rounded-lg">
        <defs>
          {/* Glow filters for different states */}
          <filter id="glow-green">
            <feGaussianBlur stdDeviation="3" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>

          {/* Gradient definitions */}
          <radialGradient id="center-gradient">
            <stop offset="0%" stopColor="rgba(59, 130, 246, 0.3)" />
            <stop offset="100%" stopColor="rgba(59, 130, 246, 0)" />
          </radialGradient>
        </defs>

        {/* Center hub */}
        <motion.circle
          cx={width / 2}
          cy={height / 2}
          r={40}
          className="fill-current text-blue-500"
          opacity={0.2}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.5 }}
        />

        {/* Pulsing center ring */}
        <motion.circle
          cx={width / 2}
          cy={height / 2}
          r={40}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="text-blue-500"
          animate={{
            r: [40, 50, 40],
            opacity: [0.8, 0.3, 0.8],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
          }}
        />

        {/* Connection lines from center to venues */}
        {venuePositions.map(({ x, y, venue }, index) => (
          <motion.line
            key={`line-${venue.id}`}
            x1={width / 2}
            y1={height / 2}
            x2={x}
            y2={y}
            stroke={getStatusColor(venue)}
            strokeWidth={selectedVenue === venue.id ? 3 : 1.5}
            strokeDasharray="5,5"
            opacity={0.4}
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 1, delay: index * 0.1 }}
          />
        ))}

        {/* Device connection lines (only for selected venue) */}
        <AnimatePresence>
          {selectedVenue && venuePositions.map(({ x: vX, y: vY, venue }) => {
            if (venue.id !== selectedVenue) return null;

            const devicePositions = getDevicePositions(vX, vY, venue.devices);
            return devicePositions.map(({ x: dX, y: dY, device }) => (
              <motion.line
                key={`device-line-${device.id}`}
                x1={vX}
                y1={vY}
                x2={dX}
                y2={dY}
                stroke={device.is_online ? '#10B981' : '#6B7280'}
                strokeWidth={1}
                opacity={0.3}
                initial={{ pathLength: 0, opacity: 0 }}
                animate={{ pathLength: 1, opacity: 0.3 }}
                exit={{ pathLength: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
              />
            ));
          })}
        </AnimatePresence>

        {/* Venue nodes */}
        {venuePositions.map(({ x, y, venue }, index) => {
          const color = getStatusColor(venue);
          const isSelected = selectedVenue === venue.id;

          return (
            <g key={venue.id}>
              {/* Glow effect when selected */}
              {isSelected && (
                <motion.circle
                  cx={x}
                  cy={y}
                  r={35}
                  fill={color}
                  opacity={0.2}
                  animate={{
                    r: [35, 45, 35],
                    opacity: [0.2, 0.05, 0.2],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                  }}
                />
              )}

              {/* Venue circle */}
              <motion.circle
                cx={x}
                cy={y}
                r={25}
                fill={color}
                className="cursor-pointer"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                whileHover={{ scale: 1.2 }}
                transition={{ duration: 0.3, delay: 0.2 + index * 0.1 }}
                onClick={() => setSelectedVenue(isSelected ? null : venue.id)}
                filter={isSelected ? "url(#glow-green)" : undefined}
              />

              {/* Online device count badge */}
              <motion.circle
                cx={x + 15}
                cy={y - 15}
                r={10}
                className="fill-current text-white dark:text-gray-900"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.3, delay: 0.5 + index * 0.1 }}
              />
              <motion.text
                x={x + 15}
                y={y - 15}
                textAnchor="middle"
                dy=".3em"
                className="text-xs font-bold fill-current text-gray-900 dark:text-white"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3, delay: 0.5 + index * 0.1 }}
              >
                {venue.online_count}
              </motion.text>
            </g>
          );
        })}

        {/* Device nodes (only for selected venue) */}
        <AnimatePresence>
          {selectedVenue && venuePositions.map(({ x: vX, y: vY, venue }) => {
            if (venue.id !== selectedVenue) return null;

            const devicePositions = getDevicePositions(vX, vY, venue.devices);
            return devicePositions.map(({ x: dX, y: dY, device }, dIndex) => (
              <motion.g key={device.id}>
                <motion.circle
                  cx={dX}
                  cy={dY}
                  r={8}
                  fill={device.is_online ? '#10B981' : '#6B7280'}
                  className="cursor-pointer"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                  whileHover={{ scale: 1.5 }}
                  transition={{ duration: 0.2, delay: dIndex * 0.05 }}
                  onHoverStart={() => setHoveredDevice(device.id)}
                  onHoverEnd={() => setHoveredDevice(null)}
                />

                {/* Violation indicator */}
                {device.active_violations > 0 && (
                  <motion.circle
                    cx={dX}
                    cy={dY}
                    r={8}
                    fill="none"
                    stroke="#EF4444"
                    strokeWidth={2}
                    initial={{ scale: 0 }}
                    animate={{
                      scale: [1, 1.5, 1],
                      opacity: [1, 0.3, 1],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                    }}
                  />
                )}
              </motion.g>
            ));
          })}
        </AnimatePresence>
      </svg>

      {/* Venue labels (positioned around the SVG) */}
      {venuePositions.map(({ x, y, venue }) => (
        <motion.div
          key={`label-${venue.id}`}
          className="absolute pointer-events-none"
          style={{
            left: x,
            top: y + 40,
            transform: 'translateX(-50%)',
          }}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <div className="bg-white dark:bg-gray-900 px-3 py-1 rounded-lg shadow-lg text-center">
            <div className="text-xs font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap">
              {venue.name}
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400">
              {venue.devices.length} devices
            </div>
          </div>
        </motion.div>
      ))}

      {/* Device tooltip */}
      <AnimatePresence>
        {hoveredDevice && venuePositions.map(({ x: vX, y: vY, venue }) => {
          if (venue.id !== selectedVenue) return null;

          const devicePositions = getDevicePositions(vX, vY, venue.devices);
          const devicePos = devicePositions.find(dp => dp.device.id === hoveredDevice);

          if (!devicePos) return null;

          return (
            <motion.div
              key={`tooltip-${hoveredDevice}`}
              className="absolute pointer-events-none z-10"
              style={{
                left: devicePos.x,
                top: devicePos.y - 50,
                transform: 'translateX(-50%)',
              }}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
            >
              <div className="bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 px-3 py-2 rounded-lg shadow-xl text-xs whitespace-nowrap">
                <div className="font-semibold">{devicePos.device.name}</div>
                <div className="flex items-center gap-2 mt-1">
                  <div className={`w-2 h-2 rounded-full ${
                    devicePos.device.is_online ? 'bg-green-500' : 'bg-gray-500'
                  }`} />
                  <span>{devicePos.device.is_online ? 'Online' : 'Offline'}</span>
                </div>
                {devicePos.device.active_violations > 0 && (
                  <div className="text-red-400 mt-1">
                    {devicePos.device.active_violations} violations
                  </div>
                )}
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>

      {/* Legend */}
      <div className="absolute bottom-4 left-4 bg-white dark:bg-gray-900 rounded-lg shadow-lg p-3">
        <div className="text-xs font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Status
        </div>
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-xs">
            <div className="w-3 h-3 rounded-full bg-green-500" />
            <span className="text-gray-700 dark:text-gray-300">Healthy</span>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <div className="w-3 h-3 rounded-full bg-yellow-500" />
            <span className="text-gray-700 dark:text-gray-300">Warning</span>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <span className="text-gray-700 dark:text-gray-300">Critical</span>
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="absolute bottom-4 right-4 bg-white dark:bg-gray-900 rounded-lg shadow-lg p-3 max-w-[200px]">
        <div className="text-xs text-gray-600 dark:text-gray-400">
          Click a venue to view its devices
        </div>
      </div>
    </div>
  );
};

export default NetworkGraph;
